import { supabase } from '../lib/supabase'
import type { DeliveryAddressPayload } from './payment.service'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

export type CarrierShipmentStatus = 'shipped' | 'in_transit' | 'delivered'

export interface GroupShipment {
  status: CarrierShipmentStatus | null
}

export type GroupOrderDeliveryAddressInput = Omit<DeliveryAddressPayload, 'owner_type'>

function mapCarrierStatus(raw?: string | null): CarrierShipmentStatus | null {
  if (!raw) return null

  const value = raw.toLowerCase().replace(/[\s-]+/g, '_')

  if (['delivered', 'entregado'].includes(value)) return 'delivered'
  if (['in_transit', 'en_transito', 'transit'].includes(value)) return 'in_transit'
  if (['shipped', 'enviado', 'expedido'].includes(value)) return 'shipped'

  return null
}

class ShippingService {
  async getGroupShipment(groupId: string): Promise<GroupShipment> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}/shipping`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      })

      if (!response.ok) {
        return { status: null }
      }

      const data = await response.json()
      return {
        status: mapCarrierStatus(data?.status ?? data?.shipping_status ?? data?.shipment_status),
      }
    } catch {
      return { status: null }
    }
  }

  /**
   * Updates the delivery address linked to a group_order via RPC.
   * Address lives in public.delivery_addresses (owner_type = 'group_order').
   */
  async updateGroupOrderDeliveryAddress(
    groupOrderId: string,
    address: GroupOrderDeliveryAddressInput
  ): Promise<{ success: boolean; address?: Record<string, unknown>; error?: string }> {
    try {
      const { data, error } = await supabase.rpc('update_group_order_delivery_address', {
        p_group_order_id: groupOrderId,
        p_recipient_name: address.recipient_name,
        p_recipient_email: address.recipient_email,
        p_recipient_phone: address.recipient_phone,
        p_recipient_line_1: address.recipient_line_1,
        p_recipient_line_2: address.recipient_line_2 ?? null,
        p_recipient_city: address.recipient_city,
        p_recipient_postal_code: address.recipient_postal_code,
        p_recipient_province: address.recipient_province,
        p_recipient_country: address.recipient_country || 'ES',
      })

      if (error) {
        return { success: false, error: error.message }
      }

      if (data && typeof data === 'object' && 'success' in data && data.success === false) {
        return {
          success: false,
          error: typeof data.error === 'string' ? data.error : 'No se pudo actualizar la dirección',
        }
      }

      const updatedAddress =
        data && typeof data === 'object' && 'address' in data
          ? (data.address as Record<string, unknown>)
          : (data as Record<string, unknown> | undefined)

      return { success: true, address: updatedAddress }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al actualizar la dirección'
      return { success: false, error: message }
    }
  }
}

export const shippingService = new ShippingService()
